import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './idb-highcharts-disaggregation.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import * as Highcharts from 'highcharts';

interface CustomPointOptions {
  institution_nm: string;
  unit_measure: string;
  data: object[];
}

/**
 * The IDB Highcharts component.
 * @element idb-highcharts-disaggregation
 * @extends OutlineElement
 */
@customElement('idb-highcharts-disaggregation')
export class IdbToggleView extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: Object, attribute: 'chart-data' })
  chartData: CustomPointOptions;

  private chart?: Highcharts.Chart;

  firstUpdated() {
    this.createChart();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('chartData') && this.chart) {
      this.chart.update(this.createSeries());
    }
  }

  // Método para inicializar el gráfico
  private createChart() {
    const container = this.shadowRoot?.getElementById(
      'chart-container'
    ) as HTMLElement;

    if (container) {
      this.chart = Highcharts.chart(container, {
        ...this.defaultOptions(),
        ...this.createSeries(),
      });
    }
  }

  render(): TemplateResult {
    return html`<div id="chart-container"></div>`;
  }

  private createSeries(): Highcharts.Options {
    if (this.chartData) {

      const customColors = [
        'var(--highchart-disaggregation-color-one)',
        'var(--highchart-disaggregation-color-four)',
        'var(--highchart-disaggregation-color-three)',
        'var(--highchart-disaggregation-color-two)',
      ];

      let data = this.chartData.data.map((point: any) => ({
        ...point,
        unit_measure: this.chartData.unit_measure,
      }));

      return {
        chart: {
          style: {
            fontFamily: 'var(--ff-display)',
          },
          backgroundColor: 'transparent',
          type: 'pie',
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
            },
            showInLegend: true,
          },
        },
        tooltip: {
          headerFormat: '<b>{point.name}</b>: ',
          formatter: function (this: Highcharts.Point) {
            const unitMeasure = (this as any).unit_measure || '';

            if (unitMeasure === '%') {
              return `<b>${this.name}</b>: ${this.y}${unitMeasure}`;
            } else if (unitMeasure === '$') {
              return `<b>${this.name}</b>: ${unitMeasure}${this.y}`;
            } else {
              return `<b>${this.name}</b>: ${this.y}`;
            }
          },
        },
        series: [
          {
            type: 'pie',
            innerSize: '60%',
            data,
            colors: customColors,
            dataLabels: {
              enabled: true,
              style: {
                fontSize: 'var(--fs-base)',
                color: '#888888',
                textOutline: '#ffffff',
              },
              formatter: function (this: Highcharts.Point) {
                const unitMeasure = (this as any).unit_measure || '';

                if (unitMeasure === '%') {
                  return this.y + unitMeasure;
                } else if (unitMeasure === '$') {
                  return unitMeasure + this.y;
                } else {
                  return this.y;
                }
              },
            },
            showInLegend: true,
          },
        ],
      };
    }
    return {};
  }

  private defaultOptions(): Highcharts.Options {
    return {
      chart: {
        backgroundColor: 'transparent',
        type: 'pie',
      },
      title: {
        text: '',
      },
      exporting: {
        enabled: false,
      },
      yAxis: {
        gridLineWidth: 0,
        title: {
          text: '',
        },
        lineWidth: 1,
        lineColor: '#000',
        plotLines: [
          {
            color: '#000',
          },
        ],
      },
      plotOptions: {
        series: {
          lineWidth: 9,
        },
      },
      tooltip: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
    };
  }
}
